import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, TrendingUp, Loader2, Lock, 
  Save, Calendar, Target, CheckCircle, Plus, Trash2
} from "lucide-react";

export const RapidMomentumPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [formData, setFormData] = useState({
    weekly_review_decisions: "",
    standards_to_protect: "",
    recalibration_signals: "",
    commitment_acknowledged: false,
    commitment_date: ""
  });

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/rapid/enrollment`, {  });
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        // Pre-fill if momentum data exists
        if (data.momentum_plan) {
          setFormData(prev => ({
            ...prev,
            ...data.momentum_plan,
            commitment_acknowledged: data.momentum_plan?.commitment_acknowledged || false
          }));
        }
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
    } finally {
      setLoading(false);
    }
  };

  const isLocked = () => {
    if (!enrollment) return true;
    // Momentum is unlocked after Session 3 is completed
    return !enrollment.session_3?.completed;
  };

  const isEngagementCompleted = () => {
    return enrollment?.status === "completed";
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const response = await authFetch(`${API}/rapid/momentum`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          ...formData,
          commitment_date: formData.commitment_acknowledged ? new Date().toISOString() : null
        })
      });

      if (response.ok) {
        toast.success("Momentum plan saved successfully!");
        fetchEnrollment();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isLocked()) {
    return (
      <div className="min-h-screen bg-neutral-50" data-testid="momentum-locked">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Lock className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Momentum Plan is Locked</h1>
          <p className="text-neutral-600 mb-6">Please complete Session 3 first to unlock your Momentum Plan.</p>
          <Button onClick={() => navigate("/rapid/session/3")} className="bg-emerald-600 hover:bg-emerald-700">
            Go to Session 3
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="rapid-momentum">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/rapid")} className="text-neutral-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-semibold text-neutral-900">Momentum Plan (30–90 Days)</h1>
                {isEngagementCompleted() && <Badge className="bg-emerald-100 text-emerald-700">Engagement Complete</Badge>}
              </div>
              <p className="text-sm text-neutral-500">R.A.P.I.D.™ Business Recalibration</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Page Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 mb-4">
            <TrendingUp className="w-8 h-8 text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Momentum Plan</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            <strong>Objective:</strong> Maintain alignment between decisions and execution.
          </p>
        </div>

        {/* Section 1: Active Decisions (Consultant fills in) */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-slate-50 border-b">
            <div className="flex items-center space-x-2">
              <Target className="w-5 h-5 text-slate-600" />
              <div>
                <CardTitle className="text-lg">Active Decisions</CardTitle>
                <CardDescription className="mt-1">
                  These decisions remain active and require consistent follow-through.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {enrollment?.momentum_plan?.active_decisions?.length > 0 ? (
              <div className="space-y-3">
                {enrollment.momentum_plan.active_decisions.map((decision, idx) => (
                  <div key={idx} className="flex items-start space-x-3 p-3 bg-neutral-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-neutral-900">{decision.decision}</p>
                      {decision.status && (
                        <p className="text-sm text-neutral-600 mt-1">Status: {decision.status}</p>
                      )}
                      {decision.due_date && (
                        <p className="text-sm text-neutral-500">Due: {decision.due_date}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Target className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>Your consultant will add your active decisions here.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Execution Rhythm (Client Editable) */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-teal-50 border-b border-teal-100">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-teal-600" />
              <CardTitle className="text-lg">Execution Rhythm</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <Label className="text-base font-medium">
                What decisions require weekly review?
              </Label>
              <Textarea
                value={formData.weekly_review_decisions}
                onChange={(e) => updateField("weekly_review_decisions", e.target.value)}
                placeholder="List the decisions you'll review each week..."
                className="min-h-24"
                data-testid="momentum-weekly"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base font-medium">
                What standards must remain protected?
              </Label>
              <Textarea
                value={formData.standards_to_protect}
                onChange={(e) => updateField("standards_to_protect", e.target.value)}
                placeholder="Define the non-negotiable standards..."
                className="min-h-24"
                data-testid="momentum-standards"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base font-medium">
                What signals tell you recalibration is needed?
              </Label>
              <Textarea
                value={formData.recalibration_signals}
                onChange={(e) => updateField("recalibration_signals", e.target.value)}
                placeholder="Identify warning signs to watch for..."
                className="min-h-24"
                data-testid="momentum-signals"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Accountability Commitments */}
        <Card className="border-emerald-200 bg-emerald-50">
          <CardHeader className="border-b border-emerald-200">
            <CardTitle className="text-lg text-emerald-900">Accountability Commitment</CardTitle>
            <CardDescription className="text-emerald-700 mt-1">
              I commit to operating from the standards established in this engagement.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="commitment"
                checked={formData.commitment_acknowledged}
                onCheckedChange={(checked) => updateField("commitment_acknowledged", checked)}
                className="mt-1"
                data-testid="momentum-commitment"
              />
              <label htmlFor="commitment" className="cursor-pointer">
                <p className="font-medium text-emerald-900">I acknowledge this commitment</p>
                <p className="text-sm text-emerald-700 mt-1">
                  By checking this box, I commit to maintaining the execution standards, reviewing decisions weekly, and applying the recalibration framework when needed.
                </p>
                {formData.commitment_acknowledged && (
                  <p className="text-sm text-emerald-600 mt-2">
                    Acknowledged on: {formData.commitment_date ? new Date(formData.commitment_date).toLocaleDateString() : new Date().toLocaleDateString()}
                  </p>
                )}
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => navigate("/rapid/session/3")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Session 3
          </Button>
          <div className="flex space-x-3">
            <Button 
              onClick={handleSave}
              className="bg-teal-600 hover:bg-teal-700"
              disabled={saving}
              data-testid="momentum-save"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Momentum Plan
                </>
              )}
            </Button>
            {isEngagementCompleted() && (
              <Button onClick={() => navigate("/rapid/completion")} className="bg-emerald-600 hover:bg-emerald-700">
                View Completion
                <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RapidMomentumPage;
